/**
 * Workspace persistence — one coalescing writer between the reducer and qv-data.
 *
 * **Neither live nor batched on a timer**, because both are wrong in opposite
 * directions. Writing on every change means ~60 requests a second while a
 * divider is dragged, of which only the last matters. Flushing on a fixed
 * interval loses whatever happened since the last tick when the tab closes. So:
 * coalesce, debounce past the end of the gesture, and force a flush on
 * `pagehide` — a drag becomes one write, and closing the tab commits it.
 *
 * **What gets written depends on what changed.** A full save re-shreds the whole
 * contract tree across fifteen tables; a pane drag needs four rows. The reducer
 * already names its actions, so the action type routes the write, and a full save
 * supersedes any granular writes queued alongside it rather than racing them.
 *
 * State that is not workspace data is not saved at all (D8) — which editor panel
 * happened to be open is not a property of the workspace.
 *
 * Without `VITE_QV_SET_URL` every hook here is inert and the app runs on the demo
 * workspace, exactly as before.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { mintUlid, type Theme, type UserWorkspace } from "@qufolio/model";
import { WorkspaceStore, type LayoutSizes, type PaneHeight } from "@qufolio/transport";

import { makeDemoWorkspace } from "../lib/demo-workspace";
import { pruneOrphanAxes } from "../lib/orphan-axes";
import { upgradePressure } from "../lib/pressure-upgrade";
import { workspaceReducer, type WorkspaceAction } from "../lib/workspace-actions";

const SET_URL = httpBase(import.meta.env.VITE_QV_SET_URL);

/**
 * Until Firebase lands there is no real uid. A constant is honest about that —
 * it keeps one row addressable so persistence can be developed and tested, and
 * it is the single place to replace when auth arrives.
 */
// Shared with `api/symbols`, which scopes the journal groups by the same id.
import { idToken } from "../lib/session";
import { actAsHeaders } from "../lib/act-as";
import { currentUserId } from "../lib/user";
import { rememberThemeRef, rememberedThemeRef } from "../lib/theme-cache";
import { httpBase } from "./backend";

/** Long enough to outlast a drag's inter-frame gaps, short enough to feel saved. */
const DEBOUNCE_MS = 600;

export type SaveStatus = "off" | "loading" | "idle" | "saving" | "error";

/** What a pending flush owes the server. */
interface PendingWrite {
  /** A full save subsumes everything below it. */
  full: boolean;
  /** Per layout; the axis that did not move is absent, never sent as empty. */
  layoutSizes: Map<string, LayoutSizes>;
  /** Per pane id, so two drags of the same pane collapse to its final height. */
  paneHeights: Map<string, string>;
}

const emptyWrite = (): PendingWrite => ({
  full: false,
  layoutSizes: new Map(),
  paneHeights: new Map(),
});

/**
 * Classify an action by the narrowest write that captures it.
 *
 * `reset` is deliberately absent: it *is* the load, and saving it back would
 * write the server's own answer straight back to the server on every boot.
 */
function planFor(action: WorkspaceAction, workspace: UserWorkspace, into: PendingWrite): void {
  switch (action.type) {
    case "setColumnSizes":
      into.layoutSizes.set(action.layoutId, {
        ...into.layoutSizes.get(action.layoutId),
        columnSizes: action.columnSizes,
      });
      return;
    case "setRowSizes":
      into.layoutSizes.set(action.layoutId, {
        ...into.layoutSizes.get(action.layoutId),
        rowSizes: action.rowSizes,
      });
      return;
    case "setDockSizes":
      into.layoutSizes.set(action.layoutId, {
        ...into.layoutSizes.get(action.layoutId),
        dockSizes: action.sizes,
      });
      return;
    case "setPaneHeights": {
      // The action carries heights positionally; the endpoint addresses panes by
      // id, so the ids come from the workspace the reducer just produced.
      const group = workspace.layouts
        .flatMap((l) => l.sections)
        .map((s) => s.config as { id?: string; panes?: { id: string; height?: string }[] })
        .find((c) => c?.id === action.chartGroupId);
      for (const pane of group?.panes ?? []) {
        if (pane.height) into.paneHeights.set(pane.id, pane.height);
      }
      return;
    }
    case "reset":
      return;
    default:
      // Columns, filters, theme, symbol, chart type — structural changes with no
      // granular endpoint. Rare enough that a full save is the simple answer.
      into.full = true;
  }
}

/**
 * What to paint before the server has said anything.
 *
 * The demo workspace, with one field overwritten: the theme this browser last
 * saw for this user. Everything else on screen is replaced when the load lands
 * and looks like *loading*; the theme is the one field whose wrong value
 * repaints the entire shell, so it is the one worth guessing. See
 * `lib/theme-cache` for why a guess is the best that is available here.
 *
 * With no `VITE_QV_SET_URL` there is no load to race, and the app is documented
 * to run on the demo workspace exactly as it always has — so the cache is not
 * consulted at all rather than quietly re-theming a backend-less build.
 */
function initialWorkspace(): UserWorkspace {
  const demo = makeDemoWorkspace();
  if (!SET_URL) return demo;
  const remembered = rememberedThemeRef(currentUserId());
  return remembered ? { ...demo, themeRef: remembered } : demo;
}

/**
 * The workspace, its dispatch, and a save indicator.
 *
 * Owns the reducer so it can see *actions*, not just resulting state: a diff of
 * two workspaces cannot tell a pane drag from a column toggle, and that
 * distinction is what keeps a drag off the full-save path.
 */
/**
 * How a layout window reaches the workspace it does not own
 * (`lib/window-sync.ts`): its edits go to the main window, and the main
 * window's workspace comes back. Stable for the page's life.
 */
export interface WorkspaceFollower {
  send: (action: WorkspaceAction) => void;
  /** Called with every workspace the main window holds; returns the unsubscribe. */
  subscribe: (onState: (workspace: UserWorkspace, status: SaveStatus) => void) => () => void;
}

export function usePersistentWorkspace(follower?: WorkspaceFollower): {
  workspace: UserWorkspace;
  dispatch: (action: WorkspaceAction) => void;
  status: SaveStatus;
  /** Themes this user authored, alongside the built-ins. */
  themes: Theme[];
  /** Save an authored theme. Written through immediately: a theme edit is a
   *  deliberate act on a small object, not a stream of drag events, so there is
   *  nothing to coalesce and waiting would only risk losing it. */
  saveTheme: (theme: Theme) => Promise<void>;
} {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [workspace, rawDispatch] = useReducer(workspaceReducer, undefined, initialWorkspace);
  const [status, setStatus] = useState<SaveStatus>(SET_URL || follower ? "loading" : "off");

  /** Reads — the authored themes — which a layout window still does for itself. */
  const reader = useMemo(
    () => (SET_URL ? new WorkspaceStore({ baseUrl: SET_URL, idToken, headers: actAsHeaders }) : null),
    [],
  );
  /**
   * Loads and saves the workspace. **None in a layout window**: the main window
   * is the one writer (`lib/window-sync.ts`), so everything below that loads,
   * saves or remembers is inert there.
   */
  const store = follower ? null : reader;

  // A layout window's workspace is the main window's, arriving as it changes.
  // `reset` is the load's own action, and deliberately unsaved.
  useEffect(() => {
    if (!follower) return;
    return follower.subscribe((next, nextStatus) => {
      rawDispatch({ type: "reset", workspace: next });
      setStatus(nextStatus);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The flush reads these at fire time rather than closing over a render's
  // values, so a save always sends the newest state and one timer serves all.
  const latest = useRef(workspace);
  latest.current = workspace;
  const pending = useRef<PendingWrite>(emptyWrite());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Whether the server holds rows for this user yet.
   *
   * The granular endpoints address a layout or pane by id and 404 when it does
   * not exist — correct of them, but it means a brand-new user whose first act
   * is dragging a divider would have that drag rejected. So until a full tree
   * has been stored, every write is promoted to a full save. It self-corrects
   * after one.
   */
  const stored = useRef(false);

  /**
   * The load failed, so this session must not write.
   *
   * **This is a data-loss guard, not a status flag.** A failed load leaves the
   * demo workspace in state and `stored` false, and "the first write is always a
   * full save" — so the next divider drag would send the *demo* tree under this
   * user's id and replace whatever the server actually held. The user would be
   * looking at a chart they did not configure while the one they did was
   * overwritten by it.
   *
   * That is exactly what nearly happened on 2026-08-04: an unapplied migration
   * (`annotation.points`) made every load throw, the app fell back to the demo
   * workspace, and only the fact that *saves* were failing for the same reason
   * kept two days of work on disk. Fixing the migration alone would have left
   * the trap armed for the next schema change.
   *
   * So a load that errors is not "no saved workspace" — it is "unknown", and the
   * only safe thing to do with an unknown remote state is refuse to overwrite
   * it. Edits still apply locally; they simply do not persist, and the status
   * stays `error` so the strip says so.
   */
  const blocked = useRef(false);

  const flush = useCallback(async () => {
    if (!store) return;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const write = pending.current;
    pending.current = emptyWrite();
    if (!write.full && !write.layoutSizes.size && !write.paneHeights.size) return;

    // Refuse rather than clobber. The load told us nothing about what the
    // server holds, and a full save would replace all of it.
    if (blocked.current) {
      setStatus("error");
      return;
    }

    setStatus("saving");
    try {
      if (write.full || !stored.current) {
        // Supersedes the granular writes queued beside it — the full tree
        // already contains those sizes, so sending both would be two writes of
        // the same fact with no ordering guarantee between them.
        await store.save(currentUserId(), latest.current);
        stored.current = true;
      } else {
        const panes: PaneHeight[] = [...write.paneHeights].map(([id, height]) => ({
          id,
          height,
        }));
        await Promise.all([
          ...[...write.layoutSizes].map(([id, sizes]) =>
            store.saveLayoutSizes(currentUserId(), id, sizes),
          ),
          ...(panes.length ? [store.savePaneHeights(currentUserId(), panes)] : []),
        ]);
      }
      setStatus("idle");
    } catch (cause) {
      // Kept visible rather than retried: every write sends whole state, so the
      // next change overwrites the failure anyway. A retry loop would only add
      // requests that the following edit invalidates.
      console.error("[persistence] save failed", cause);
      setStatus("error");
    }
  }, [store]);

  const dispatch = useCallback(
    (action: WorkspaceAction) => {
      rawDispatch(action);
      // Applied here at once, so a drag answers the pointer; the main window
      // applies it again, saves, and sends back the workspace that wins.
      if (follower) {
        follower.send(action);
        return;
      }
      if (!store) return;
      // The reducer is pure, so the post-action workspace is available here —
      // no need to wait a render to learn which pane ids moved.
      planFor(action, workspaceReducer(latest.current, action), pending.current);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [store, flush, follower],
  );

  // Load once. A null answer means this user has never saved, so the demo
  // workspace stands — the app is usable before the first write, not blank.
  useEffect(() => {
    if (!store) return;
    let cancelled = false;
    void (async () => {
      try {
        const saved = await store.load(currentUserId());
        if (cancelled) return;
        if (saved) {
          // A Pressure added before Titanic gains its row here (once; see the
          // module). Saved with the next edit, like everything else in a load.
          // An axis left behind by an indicator removed before 2026-10-31 goes
          // the same way (`lib/orphan-axes.ts`).
          rawDispatch({ type: "reset", workspace: pruneOrphanAxes(upgradePressure(saved, mintUlid)) });
          stored.current = true;
        }
        setStatus("idle");
      } catch (cause) {
        if (cancelled) return;
        console.error("[persistence] load failed", cause);
        // Every write from here is refused — see `blocked`.
        blocked.current = true;
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store]);

  /**
   * Remember the theme for the next boot — the other half of `initialWorkspace`.
   *
   * **Only once the load has answered.** While `status` is `loading` the theme
   * on screen is the seed, or the demo's Classic if there was nothing to seed
   * from, and writing that back would teach the cache the very value it exists
   * to avoid. `error` is excluded for the stronger reason: a load that failed
   * said nothing about this user's theme, and overwriting a good cached answer
   * with the fallback would make the next boot flicker too.
   *
   * Keyed on `themeRef` rather than the workspace, so a pane drag does not
   * touch storage.
   */
  useEffect(() => {
    if (!store || status === "loading" || status === "error") return;
    rememberThemeRef(currentUserId(), workspace.themeRef);
  }, [store, status, workspace.themeRef]);

  // `pagehide` fires on close, navigation and bfcache freeze, where `unload`
  // does not fire reliably at all; `visibilitychange` to hidden covers a phone
  // being backgrounded, which may never fire `pagehide`.
  useEffect(() => {
    if (!store) return;
    const commit = () => void flush();
    const onHidden = () => {
      if (document.visibilityState === "hidden") commit();
    };
    window.addEventListener("pagehide", commit);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", commit);
      document.removeEventListener("visibilitychange", onHidden);
      commit(); // an unmount is a close as far as unsaved work is concerned
    };
  }, [store, flush]);

  // Themes are stored per user rather than inside the workspace: the contract
  // has `themeRef` and no `themes[]`, and one palette is shared across every
  // workspace a person has rather than copied into each.
  useEffect(() => {
    if (!reader) return;
    let cancelled = false;
    void reader
      .loadThemes(currentUserId())
      .then((loaded) => {
        if (!cancelled) setThemes(loaded);
      })
      .catch((cause) => {
        // Not fatal: the built-ins still work, so a failure here costs the
        // authored ones rather than the ability to render.
        console.error("[persistence] themes failed to load", cause);
      });
    return () => {
      cancelled = true;
    };
  }, [reader]);

  const saveTheme = useCallback(
    async (theme: Theme) => {
      setThemes((previous) => {
        const known = previous.some((t) => t.id === theme.id);
        return known ? previous.map((t) => (t.id === theme.id ? theme : t)) : [...previous, theme];
      });
      if (!store) return;
      setStatus("saving");
      try {
        await store.saveTheme(currentUserId(), theme);
        setStatus("idle");
      } catch (cause) {
        console.error("[persistence] theme save failed", cause);
        setStatus("error");
      }
    },
    [store],
  );

  return { workspace, dispatch, status, themes, saveTheme };
}
